import type { QuickViewOverlayProps } from './quickViewOverlay/QuickViewOverlay';
import type { Product } from '../../../utilities/graphql/codegen';
import type { ReactElement, MouseEvent } from 'react';

import dynamic from 'next/dynamic';
import { useState, useEffect, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { useAppContext } from '../../../utilities/context/static/AppContext';
import { useDataLayerContext } from '../../../utilities/context/static/DataLayerContext';
import { useStaticContext } from '../../../utilities/context/static/StaticContext';
import { fetchProduct } from '../../../utilities/graphql/queries';
import { getProductDataLayerEvent } from '../../../utilities/vendors';
import QuickViewIcon from '../core/icons/QuickViewIcon';
import { generateExponeaViewItemEventProperties } from '../vendors/generateExponeaViewItemEventProperties';
import { useAlgoliaInsights } from '../vendors/useAlgoliaInsights';
import { useApolloContext } from '~/campaign/core/context/ApolloContext';
import type { StyliticsPlacement } from '~/components/shared/recommendations/stylitics/types';
import { PRODUCT_NAME_VARIABLE } from '~/utilities/constants/amplienceVariables';
import { FULLSTORY_EVENTS } from '~/utilities/constants/fullstoryEvents';
import { PLP_NAVIGATED_PRODUCT } from '~/utilities/constants/localStorageKeys';
import { PDPDynamicContextProvider } from '~/utilities/context/dynamic/PDPDynamicContext';
import { useQuickViewContext } from '~/utilities/context/dynamic/QuickViewContext';
import { PDPContextProvider } from '~/utilities/context/static/PDPContext';
import { trackFullstoryEvent } from '~/utilities/helpers/syncGlobalObject';

const QuickViewOverlay = dynamic<QuickViewOverlayProps>(
  () => import('./quickViewOverlay/QuickViewOverlay').then(mod => mod.QuickViewOverlay),
  { ssr: false }
);

const S = {
  QuickView: styled.div``,
  QuickViewButton: styled.button`
    ${({ theme }) => css`
      display: flex;
      align-items: center;
      order: 1;
      transform: translateY(0);
      width: ${theme.size[10]};
      height: ${theme.size[10]};
      align-self: flex-end;
      background-color: ${theme.colors.background.component.glass};
      padding: ${theme.padding.xs};
      margin-right: ${theme.spacing.x.xs};
    `}
  `,
};

interface QuickViewProps {
  code?: string;
  pushClickEvent?: () => void;
  productName?: string;
  dataLayerQuickViewProductTileType?: string;
  algoliaClickEventName?: string;
  algoliaAddToCartEventName: string;
  algoliaAddToCartAfterSearchEventName: string;
  autoOpenOnCodeChange?: boolean;
  onClose?: () => void;
  onOpen?: (product: Product) => void;
  onViewDetails?: (code: string) => () => void;
  placement?: StyliticsPlacement;
  tabIndex?: number;
}

export const QuickView = ({
  code,
  pushClickEvent,
  productName,
  dataLayerQuickViewProductTileType = '',
  algoliaClickEventName = '',
  algoliaAddToCartEventName,
  algoliaAddToCartAfterSearchEventName,
  autoOpenOnCodeChange = false,
  onClose,
  onOpen,
  onViewDetails,
  placement,
  tabIndex = 0,
}: QuickViewProps): ReactElement => {
  const { locale, siteType } = useAppContext();
  const { client: apolloClient } = useApolloContext();
  const { pushToDataLayer } = useDataLayerContext();
  const {
    accessibilityLabels,
    configuration: { productTileConfiguration, enableExponea, defaultCurrency },
  } = useStaticContext();
  const { pushToAlgolia } = useAlgoliaInsights();
  const { setIsQuickViewOpen } = useQuickViewContext();

  const [isOpened, setIsOpened] = useState(false);
  const [fetchedProduct, setFetchedProduct] = useState<Product | undefined>(undefined);

  const quickViewButtonAriaLabel =
    productName && accessibilityLabels?.quickView?.openQuickViewWithProductName
      ? accessibilityLabels.quickView.openQuickViewWithProductName.replace(
          PRODUCT_NAME_VARIABLE,
          productName
        )
      : `Open quick view for ${productName}`;

  useEffect(() => {
    setIsQuickViewOpen(isOpened);
  }, [isOpened, setIsQuickViewOpen]);

  const preventDefault = (e: MouseEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const emitExponeaEvent = (eventProduct: Product) => {
    if (enableExponea && eventProduct) {
      const exponeaViewItemEventProperties = generateExponeaViewItemEventProperties(
        eventProduct,
        defaultCurrency
      );

      pushToDataLayer({
        event: 'exponea_view_item',
        event_name: 'view_item',
        namespace: 'exponea',
        event_properties: exponeaViewItemEventProperties,
      });
    }
  };

  const pushOpenCloseEvent = useCallback(
    (type: 'open' | 'close') => {
      pushToDataLayer({
        event: 'quickshop',
        type,
      });
    },
    [pushToDataLayer]
  );

  const handleClose = useCallback(
    (type?: string) => {
      onClose?.();

      if (type === 'BUTTON') {
        pushOpenCloseEvent('close');
      }

      sessionStorage?.removeItem(PLP_NAVIGATED_PRODUCT);
      setIsOpened(false);
    },
    [pushOpenCloseEvent, onClose]
  );

  const handleOpenQuickViewOverlay = async () => {
    if (!code) {
      return;
    }

    const productResponse = await fetchProduct({ siteType, code, locale, apolloClient });

    if (!productResponse || productResponse.errors) {
      // eslint-disable-next-line no-console
      console.log(`product request returned error for ${code}`);
    }

    if (productResponse.product) {
      setFetchedProduct(productResponse.product);
      setIsOpened(true);
      onOpen?.(productResponse.product);
      emitExponeaEvent(productResponse.product);
      pushOpenCloseEvent('open');

      const eecProductDetailViewEvent = getProductDataLayerEvent(
        'eecProductDetailView',
        productResponse.product,
        true,
        dataLayerQuickViewProductTileType
      );

      if (eecProductDetailViewEvent) {
        pushToDataLayer(eecProductDetailViewEvent);
      }

      pushClickEvent?.();
      trackFullstoryEvent(FULLSTORY_EVENTS.quickShopOpened, { product_code: code });
      pushToAlgolia(
        'clickedObjectIDsAfterSearch',
        algoliaClickEventName,
        productResponse.product.algoliaObjectId
      );
    } else {
      // eslint-disable-next-line no-console
      console.log('product is empty');
    }
  };

  useEffect(() => {
    if (autoOpenOnCodeChange && code && !isOpened) {
      handleOpenQuickViewOverlay();
    }
  }, [code]); // eslint-disable-line react-hooks/exhaustive-deps

  return productTileConfiguration?.showQuickViewOnRecommendations ? (
    <S.QuickView>
      <S.QuickViewButton
        className="icon"
        onClick={(e: MouseEvent<HTMLElement>) => {
          preventDefault(e);
          handleOpenQuickViewOverlay();
        }}
        tabIndex={tabIndex}
        data-testid="product-tile-quick-view"
        aria-label={quickViewButtonAriaLabel}
        aria-haspopup="true"
        aria-expanded={isOpened && !!fetchedProduct}
      >
        <QuickViewIcon />
      </S.QuickViewButton>
      {isOpened && fetchedProduct && (
        <PDPContextProvider product={fetchedProduct}>
          <PDPDynamicContextProvider>
            <QuickViewOverlay
              onClose={handleClose}
              onViewDetails={onViewDetails}
              code={code!}
              product={fetchedProduct}
              algoliaAddToCartEventName={algoliaAddToCartEventName}
              algoliaAddToCartAfterSearchEventName={algoliaAddToCartAfterSearchEventName}
              placement={placement}
            />
          </PDPDynamicContextProvider>
        </PDPContextProvider>
      )}
    </S.QuickView>
  ) : (
    <></>
  );
};
